import { useState } from 'react';

import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Book,
  LifeBuoy,
  Phone,
  Rocket,
  Settings,
  Timer,
} from 'lucide-react';
import { motion } from 'motion/react';

import { DiagramContainer } from '@src/components/DiagramContainer';
import AnswerSection from '@src/components/seo/AnswerSection';
import { Button } from '@src/components/ui/button';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

const BOSSS_BLUE = '#2973a3'; // Updated to match other pages
const BG_COLOR = '#F8FAFC';
const TEXT_COLOR = '#384656';
const DARK_TEXT = '#1C2534'; // For labels

// Helper function for precise line connections in circular layout
const calculateCircularPosition = (
  centerX,
  centerY,
  radius,
  angle,
) => {
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle),
  };
};

// Helper function to calculate line endpoints that touch circle circumference
const calculateLineEndpoints = (
  x1,
  y1,
  r1,
  x2,
  y2,
  r2,
) => {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  return {
    x1: x1 + r1 * Math.cos(angle),
    y1: y1 + r1 * Math.sin(angle),
    x2: x2 - r2 * Math.cos(angle),
    y2: y2 - r2 * Math.sin(angle),
  };
};

// Support Services Circular Diagram
function SupportServicesCircularDiagram() {
  const [hoveredService, setHoveredService] = useState(
    null,
  );

  const centerX = 200;
  const centerY = 160; // Moved down to center better in taller viewBox
  const radius = 105; // Optimized for even spacing while keeping circles within bounds
  const centralRadius = 48; // Keep central circle size
  const serviceRadius = 46; // Slightly adjusted for perfect fit

  const services = [
    {
      label: 'Dedicated Help Desk',
      color: '#10B981',
      icon: Phone,
      angle: -Math.PI / 2, // Top
    },
    {
      label: 'Incident Response',
      color: '#F59E0B',
      icon: AlertTriangle,
      angle: -Math.PI / 2 + (2 * Math.PI) / 5, // Top-right
    },
    {
      label: 'System Monitoring',
      color: '#EF4444',
      icon: Activity,
      angle: -Math.PI / 2 + (4 * Math.PI) / 5, // Bottom-right
    },
    {
      label: 'Knowledge Base',
      color: '#8B5CF6',
      icon: Book,
      angle: -Math.PI / 2 + (6 * Math.PI) / 5, // Bottom-left
    },
    {
      label: 'SLA Guarantee',
      color: BOSSS_BLUE,
      icon: Timer,
      angle: -Math.PI / 2 + (8 * Math.PI) / 5, // Top-left
    },
  ];

  const servicePositions = services.map((service) => ({
    ...service,
    ...calculateCircularPosition(
      centerX,
      centerY,
      radius,
      service.angle,
    ),
  }));

  return (
    <DiagramContainer className="w-full max-w-xl mx-auto">
      <svg viewBox="0 0 400 320" className="w-full h-auto">
        {/* Central Hub */}
        <motion.circle
          cx={centerX}
          cy={centerY}
          r={centralRadius}
          fill={BOSSS_BLUE}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            filter: 'drop-shadow(0 6px 20px rgba(41, 115, 163, 0.4))',
          }}
        />

        <motion.text
          x={centerX}
          fill="white"
          fontSize="13"
          y={centerY - 6}
          fontWeight="bold"
          textAnchor="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          BOSSS
        </motion.text>

        <motion.text
          x={centerX}
          fill="white"
          fontSize="11"
          y={centerY + 8}
          fontWeight="bold"
          textAnchor="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          Support
        </motion.text>

        {/* Connection lines from center border to service borders */}
        {servicePositions.map((service, i) => {
          const endpoints = calculateLineEndpoints(
            centerX,
            centerY,
            centralRadius,
            service.x,
            service.y,
            serviceRadius,
          );
          return (
            <motion.line
              key={`line-${i}`}
              x1={endpoints.x1}
              y1={endpoints.y1}
              x2={endpoints.x2}
              y2={endpoints.y2}
              strokeWidth="2.5"
              stroke={BOSSS_BLUE}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
            />
          );
        })}

        {/* Service Nodes */}
        {servicePositions.map((service, i) => {
          const IconComponent = service.icon;
          return (
            <g key={i}>
              <motion.circle
                fill="white"
                cx={service.x}
                cy={service.y}
                r={serviceRadius}
                strokeWidth="1.5"
                stroke={service.color}
                initial={{ scale: 0 }}
                animate={{
                  scale: hoveredService === service.label ? 1.05 : 1,
                }}
                transition={{
                  duration: 0.5,
                  delay: 0.7 + i * 0.1,
                }}
                style={{
                  filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))',
                  cursor: 'pointer',
                }}
                onMouseLeave={() => setHoveredService(null)}
                onMouseEnter={() => setHoveredService(service.label)}
              />

              {/* Service icon - Centered above label with consistent positioning */}
              <motion.foreignObject
                width="24"
                height="20"
                x={service.x - 12}
                y={service.y - 22}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ pointerEvents: 'none' }}
                transition={{
                  duration: 0.3,
                  delay: 1.0 + i * 0.1,
                }}
              >
                <div className="w-6 h-5 flex items-center justify-center">
                  <IconComponent
                    className="w-5 h-5"
                    style={{
                      color: service.color,
                      strokeWidth: '1.5px',
                    }}
                  />
                </div>
              </motion.foreignObject>

              {/* Service label - Perfectly contained with consistent padding */}
              <motion.foreignObject
                width="76"
                height="32"
                y={service.y + 6}
                x={service.x - 38}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ pointerEvents: 'none' }}
                transition={{
                  duration: 0.3,
                  delay: 1.2 + i * 0.1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    height: '100%',
                    fontSize: '10px',
                    fontWeight: '500',
                    color: DARK_TEXT,
                    lineHeight: '1.2',
                    padding: '3px 4px',
                    overflow: 'hidden',
                  }}
                >
                  {service.label}
                </div>
              </motion.foreignObject>
            </g>
          );
        })}
      </svg>
    </DiagramContainer>
  );
}

function SectionIntro({ title, text }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-6">
        Support Services
      </div>

      <motion.h1
        viewport={{ once: true }}
        style={{ color: DARK_TEXT }}
        transition={{ duration: 0.8 }}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        className="text-4xl lg:text-5xl font-bold"
      >
        {title}
      </motion.h1>

      <motion.p
        viewport={{ once: true }}
        style={{ color: TEXT_COLOR }}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        className="text-xl max-w-3xl mx-auto"
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        {text}
      </motion.p>
    </div>
  );
}

const servicesOffered = [
  {
    icon: Phone,
    title: 'Dedicated Help Desk',
    description: 'Our team is available during business hours to provide support for any issues you encounter, ensuring minimal disruption to your operations.',
  },
  {
    icon: AlertTriangle,
    title: 'Proactive Incident Response',
    description: 'We help you prepare for and respond to security incidents with a well-defined plan, minimizing impact and ensuring a swift recovery.',
  },
  {
    icon: Activity,
    title: 'System & Health Monitoring',
    description: 'We provide continuous monitoring of your BOSSS platform to detect and resolve potential performance issues before they affect your business.',
  },
  {
    icon: Book,
    title: 'Comprehensive Knowledge Base',
    description: 'Access our extensive library of guides, tutorials, and FAQs to find quick answers and master the BOSSS platform at your own pace.',
  },
  {
    icon: Timer,
    title: 'Service Level Agreements (SLAs)',
    description: 'We offer guaranteed response and resolution times, ensuring you receive the highest level of service and support when you need it most.',
  },
];

const steps = [
  {
    icon: LifeBuoy,
    title: '1. Problem Diagnosis',
    description: 'We start by thoroughly understanding your issue through detailed communication and remote diagnostics to identify the root cause.',
  },
  {
    icon: Settings,
    title: '2. Solution & Resolution',
    description: 'Our experts work diligently to apply the most effective solution, providing clear updates and ensuring the problem is fully resolved.',
  },
  {
    icon: Rocket,
    title: '3. Post-Resolution Review',
    description: 'After resolution, we perform a final check and document the solution, empowering you to prevent similar issues in the future.',
  },
];

export function SupportServicesPage() {
  const onNavigateToTop = useNavigateToTop();
  return (
    <div className="min-h-screen" style={{ backgroundColor: BG_COLOR }}>
      {/* Hero Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Text Content */}
            <motion.div
              className="space-y-8"
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-6">
                  Support Services
                </div>

                <h1 style={{ color: DARK_TEXT }} className="text-4xl lg:text-5xl font-bold">
                  Reliable Support, When You Need It.
                </h1>

                <p style={{ color: TEXT_COLOR }} className="text-xl leading-relaxed">
                  Our dedicated support team is here to ensure your BOSSS platform runs smoothly, providing expert assistance and timely solutions to any issue.
                </p>
              </div>

              <motion.div
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Button
                  style={{ backgroundColor: BOSSS_BLUE, color: 'white' }}
                  className="px-8 py-4 text-lg rounded-lg font-semibold transition-all duration-200 hover:shadow-xl"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Get Support Now
                </Button>
              </motion.div>
            </motion.div>

            {/* Right Column - Diagram */}
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 50 }}
            >
              <SupportServicesCircularDiagram />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/services/support" />


      {/* Services Offered Section */}
      <section className="py-20 lg:py-32" style={{ backgroundColor: 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            title="The Support You Deserve"
            text="From a dedicated help desk to proactive system monitoring, our support services are designed to give you peace of mind."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesOffered.map((service, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 + index * 0.05 }}
                className="flex flex-col items-center text-center p-6 rounded-xl border border-gray-200"
              >
                <service.icon className="w-12 h-12 mb-4" style={{ color: BOSSS_BLUE }} />

                <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">
                  {service.title}
                </h3>

                <p className="text-sm" style={{ color: TEXT_COLOR }}>
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            title="Our Three-Step Support Process"
            text="We've streamlined our process to ensure you get the help you need as efficiently as possible."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                style={{ backgroundColor: 'white' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex flex-col items-center text-center p-6 rounded-xl border border-gray-200"
              >
                <div
                  style={{ backgroundColor: `${BOSSS_BLUE}1a` }} // 1a = ~10% opacity in hex
                  className="relative w-16 h-16 rounded-full flex items-center justify-center mb-4"
                >
                  <div
                    className="absolute w-12 h-12 rounded-full"
                    style={{ backgroundColor: `${BOSSS_BLUE}0d` }} // 0d = ~5% opacity
                  />

                  <div
                    className="absolute w-8 h-8 rounded-full"
                    style={{ backgroundColor: `${BOSSS_BLUE}1a` }}
                  />

                  <div
                    style={{ backgroundColor: `${BOSSS_BLUE}33` }} // 33 = ~20% opacity
                    className="relative z-10 p-2 rounded-full flex items-center justify-center"
                  >
                    <step.icon className="w-6 h-6" style={{ color: BOSSS_BLUE }} />
                  </div>
                </div>

                <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">{step.title}</h3>
                <p className="text-sm" style={{ color: TEXT_COLOR }}>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section
        className="py-20 lg:py-32"
        style={{
          background: `linear-gradient(to bottom, transparent, ${BOSSS_BLUE}10)`,
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2
              style={{ color: TEXT_COLOR }}
              className="text-3xl lg:text-4xl font-bold mb-6"
            >
              Need Help? We&apos;re Here For You
            </h2>

            <p
              className="text-xl mb-8 max-w-2xl mx-auto"
              style={{ color: TEXT_COLOR, opacity: 0.8 }}
            >
              Our expert support team is ready to help you resolve any issues, answer questions, and ensure your BOSSS platform operates at peak performance.
            </p>

            <motion.div
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.05 }}
            >
              <Button
                style={{ backgroundColor: BOSSS_BLUE }}
                className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                onClick={() => onNavigateToTop('/contact')}
              >
                Get Support Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default SupportServicesPage;
